<script>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'EditMessageModal',
  components: {
    NextButton,
  },
  props: {
    messageContent: {
      type: String,
      required: true,
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  emits: ['save', 'close'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const editedContent = ref('');
    const isSaving = ref(false);
    const textareaRef = ref(null);

    const hasChanges = () => {
      return editedContent.value.trim() !== props.messageContent.trim();
    };

    const isValid = () => {
      return editedContent.value.trim().length > 0;
    };

    const handleSave = () => {
      if (!isValid() || !hasChanges()) return;
      isSaving.value = true;
      emit('save', editedContent.value.trim());
    };

    const handleCancel = () => {
      emit('close');
      props.onClose();
    };

    onMounted(() => {
      editedContent.value = props.messageContent;
      // Focus the textarea after mounting
      setTimeout(() => {
        if (textareaRef.value) {
          textareaRef.value.focus();
          textareaRef.value.setSelectionRange(
            textareaRef.value.value.length,
            textareaRef.value.value.length
          );
        }
      }, 100);
    });

    return {
      editedContent,
      isSaving,
      textareaRef,
      hasChanges,
      isValid,
      handleSave,
      handleCancel,
      t,
    };
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto max-w-lg w-full">
    <woot-modal-header
      :header-title="t('CONVERSATION.CONTEXT_MENU.EDIT_MESSAGE.TITLE')"
      :header-content="t('CONVERSATION.CONTEXT_MENU.EDIT_MESSAGE.DESCRIPTION')"
    />

    <div class="p-4">
      <textarea
        ref="textareaRef"
        v-model="editedContent"
        rows="5"
        class="w-full p-3 border border-slate-200 dark:border-slate-600 rounded-lg
               focus:outline-none focus:ring-2 focus:ring-woot-500
               bg-white dark:bg-slate-800
               text-slate-800 dark:text-slate-100
               resize-none"
        :placeholder="t('CONVERSATION.CONTEXT_MENU.EDIT_MESSAGE.PLACEHOLDER')"
        @keydown.ctrl.enter="handleSave"
        @keydown.meta.enter="handleSave"
      />
      <p class="mt-2 text-xs text-slate-500 dark:text-slate-400">
        {{ t('CONVERSATION.CONTEXT_MENU.EDIT_MESSAGE.HINT') }}
      </p>
    </div>

    <div class="flex justify-end gap-2 p-4 border-t border-slate-200 dark:border-slate-600">
      <NextButton
        faded
        slate
        :label="t('CONVERSATION.CONTEXT_MENU.EDIT_MESSAGE.CANCEL')"
        @click="handleCancel"
      />
      <NextButton
        :label="t('CONVERSATION.CONTEXT_MENU.EDIT_MESSAGE.SAVE')"
        :disabled="!isValid() || !hasChanges() || isSaving"
        :is-loading="isSaving"
        @click="handleSave"
      />
    </div>
  </div>
</template>
